import { useCallback, useState } from 'react';
import { type ProcessingFlags, getProcessing, setProcessing } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { Badge } from './ui.js';

/**
 * Internal kill-switches for an account: pause statement ingest and/or the AI
 * assistant (e.g. unpaid / no payment method). Enabled by default.
 */
export function ProcessingToggles({ accountId }: { accountId: string }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => getProcessing(accountId), [accountId]),
  );
  const [busy, setBusy] = useState(false);

  const toggle = async (key: keyof ProcessingFlags) => {
    if (!data) return;
    setBusy(true);
    try {
      await setProcessing(accountId, { [key]: !data[key] });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <span className="text-xs text-rose-600">Couldn’t load processing flags</span>;
  if (!data) return <span className="text-xs text-neutral-400">Loading…</span>;

  const row = (label: string, key: keyof ProcessingFlags) => {
    const paused = data[key];
    return (
      <div className="flex items-center justify-between gap-3 py-1.5">
        <span className="text-sm text-neutral-700">{label}</span>
        <div className="flex items-center gap-2">
          <Badge tone={paused ? 'rose' : 'emerald'}>{paused ? 'Paused' : 'Enabled'}</Badge>
          <button
            type="button"
            disabled={busy}
            onClick={() => toggle(key)}
            className="rounded-md border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 transition hover:bg-neutral-100 disabled:opacity-50"
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">
        Processing (internal)
      </div>
      {row('Statement ingest', 'processingPaused')}
      {row('AI assistant', 'aiPaused')}
    </div>
  );
}
